import React, { useState, useEffect } from "react";
import {
  IRaffleDefaultRewardDTO,
  IRaffleDefaultRewardMediaDTO,
  IRaffleDefaultRewardCategoryDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { InputCurrency } from "@components/forms/input-currency";
import { RaffleCreateIsValidRewardPrice } from "@hooks/functions/Raffle/create";
import { RichSelect } from "@components/forms/selectWithDescription";
import { TextAreaQuill } from "@components/forms/textarea-quill/textarea-quill";
import { ModalRaffleChangeRewardMedias } from "@components/modals/modal-raffle-edit-reward-medias";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import Swal from "sweetalert2";
import { ImageThumbUploader } from "@components/forms/img-thumb";
import { api } from "@service/api";
import { ISelectOptionDTO } from "@hooks/dtos/forms/ISelectDTO";

interface DraggableRewardsTabsProps {
  rewards: IRaffleDefaultRewardDTO[];
  onRewardsChange: (updatedRewards: IRaffleDefaultRewardDTO[]) => void;
  onEditingReward: (isEditingReward: boolean) => void;
  maxRewards?: number;
}

export const DraggableRewardsTabs = ({
  rewards,
  onRewardsChange,
  onEditingReward,
  maxRewards = 10,
}: DraggableRewardsTabsProps) => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [rewardCategories, setRewardCategories] = useState<ISelectOptionDTO[]>(
    [],
  );
  const [isLoadingRewardCategories, setIsLoadingRewardCategories] =
    useState(true);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingReward, setEditingReward] =
    useState<IRaffleDefaultRewardDTO | null>(null);

  const [selectRewardConditions] = useState<
    {
      value: number;
      name: string;
      description: string;
    }[]
  >([
    {
      value: 1,
      name: "Novo",
      description: "Produto Novo",
    },
    {
      value: 2,
      name: "Semi-novo",
      description: "Produto Semi-novo",
    },
    {
      value: 3,
      name: "Em bom estado",
      description: "Produto em bom estado de conservação",
    },
    {
      value: 4,
      name: "Usado",
      description: "Produto usado",
    },
    {
      value: 5,
      name: "Com defeito",
      description: "Produto com defeito ou defeitos leves",
    },
    {
      value: 6,
      name: "Não Aplicável",
      description: "Não aplicável, para valores monetários, etc",
    },
  ]);

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (isEditing) {
      return pushToastify({
        message: "Salve as edições antes de reordenar os prêmios!",
        type: "warning",
        theme: "dark",
      });
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newRewards = [...rewards];
    const draggedReward = newRewards[draggedIndex];

    // ---- Remove Item from Original Position --- //
    newRewards.splice(draggedIndex, 1);

    // --- Insert a new position --- //
    newRewards.splice(dropIndex, 0, draggedReward);

    // --- Update the Order --- //
    const updatedRewards = newRewards.map((reward, index) => ({
      ...reward,
      order: index + 1,
    }));

    onRewardsChange(updatedRewards);

    // --- If needs, adjust the Active Tab --- //
    if (activeTab === draggedIndex) {
      setActiveTab(dropIndex);
    } else if (activeTab === dropIndex) {
      setActiveTab(draggedIndex < dropIndex ? dropIndex - 1 : dropIndex + 1);
    } else if (
      activeTab > Math.min(draggedIndex, dropIndex) &&
      activeTab <= Math.max(draggedIndex, dropIndex)
    ) {
      setActiveTab(draggedIndex < dropIndex ? activeTab - 1 : activeTab + 1);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Edit handlers
  const handleEditReward = (reward: IRaffleDefaultRewardDTO) => {
    setEditingReward({ ...reward });
  };

  const handleInputChange = (
    field: keyof IRaffleDefaultRewardDTO,
    value: string | number,
  ) => {
    if (!editingReward) return;

    setEditingReward({
      ...editingReward,
      [field]: value,
    });
  };

  const handleSaveReward = () => {
    if (!editingReward) return;

    if (!editingReward.category || !editingReward.condition) {
      pushToastify({
        message: "Selecione a categoria e o estado do prêmio antes de salvar.",
        type: "warning",
        theme: "dark",
      });
      return;
    }

    const updatedRewards = rewards.map((reward) =>
      reward.order === editingReward.order ? editingReward : reward,
    );

    onRewardsChange(updatedRewards);
    setEditingReward(null);
  };

  const handleCancelEdit = () => {
    setEditingReward(null);
  };

  // Add new Reward
  const handleAddReward = () => {
    const nextOrder = Math.max(...rewards.map((r) => r.order), 0) + 1;
    const newReward: IRaffleDefaultRewardDTO = {
      order: nextOrder,
      condition: 0,
      category: 0,
      name: `Prêmio ${nextOrder}`,
      description_short: "",
      description: "",
      price: 0,
      productCost: 0,
      thumb: "",
      medias: [] as IRaffleDefaultRewardMediaDTO[],
    };

    const updatedRewards = [...rewards, newReward];
    onRewardsChange(updatedRewards);
    setActiveTab(updatedRewards.length - 1);
  };

  // Delete Reward
  const handleDeleteReward = async (order: number) => {
    const result = await Swal.fire({
      title: "Excluir prêmio?",
      text: "Esta alteração será aplicada quando você salvar todos os prêmios.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    const updatedRewards = rewards
      .filter((reward) => reward.order !== order)
      .map((reward, index) => ({ ...reward, order: index + 1 }));

    onRewardsChange(updatedRewards);

    if (activeTab >= updatedRewards.length && updatedRewards.length > 0) {
      setActiveTab(updatedRewards.length - 1);
    } else if (updatedRewards.length === 0) {
      setActiveTab(0);
    }
  };

  useEffect(() => {
    const loadRewardCategories = async () => {
      try {
        const response = await api.get<IRaffleDefaultRewardCategoryDTO[]>(
          "/raffle/rewardCategories?status=a",
        );
        setRewardCategories(
          response.data.map((category) => ({
            value: category.id,
            name: category.name,
            description: category.description_short,
          })),
        );
      } catch (error: any) {
        pushToastify({
          message:
            error?.response?.data?.message ||
            "Falha ao carregar as categorias de prêmio.",
          type: "error",
          theme: "dark",
        });
      } finally {
        setIsLoadingRewardCategories(false);
      }
    };

    void loadRewardCategories();
  }, []);

  useEffect(() => {
    if (rewards.length > 0 && activeTab >= rewards.length) {
      setActiveTab(0);
    }
  }, [rewards.length, activeTab]);

  useEffect(() => {
    onEditingReward(editingReward ? true : false);
  }, [editingReward]);

  useEffect(() => {
    if (rewards[activeTab]) {
      console.log({ medias: rewards[activeTab].medias });
    }
  }, [rewards]);

  // Se não há prêmios, mostra tela para adicionar o primeiro
  if (!rewards || rewards.length === 0) {
    return (
      <div className="text-center p-4">
        <div className="mb-4">
          <i className="fas fa-trophy fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">Nenhum prêmio cadastrado</h5>
          <p className="text-muted">
            Adicione o primeiro prêmio da sua campanha!
          </p>
        </div>

        <button className="btn btn-primary btn-lg" onClick={handleAddReward}>
          <i className="fas fa-plus me-2"></i>
          Adicionar Primeiro Prêmio
        </button>
      </div>
    );
  }

  const currentReward = rewards[activeTab];
  const isEditing =
    editingReward && editingReward.order === currentReward?.order;

  return (
    <div className="draggable-rewards-container">
      {/* Header com botão de adicionar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <i className="fas fa-trophy me-2 text-warning"></i>
          Prêmio{rewards.length > 1 ? `s` : ``} da Campanha ({rewards.length})
        </h6>

        {rewards.length < maxRewards && (
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              // --- Check if have some Reward be Editin' --- //
              if (isEditing) {
                return pushToastify({
                  message: "Salve as edições antes de mudar de prêmio!",
                  type: "warning",
                  theme: "dark",
                });
              }

              handleAddReward();
            }}
            title="Adicionar novo prêmio"
          >
            <i className="fas fa-plus me-1"></i>
            Adicionar Prêmios
          </button>
        )}
      </div>

      {/* Reward Tabs*/}
      <div className="nav nav-tabs mb-3">
        {rewards.map((reward, index) => (
          <div
            key={reward.order}
            className={`nav-item ${
              draggedIndex === index ? "opacity-50" : ""
            } ${dragOverIndex === index ? "border-primary border-2" : ""}`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            style={{ cursor: "grab" }}
          >
            <button
              className={`nav-link ${
                activeTab === index ? "active" : ""
              } d-flex align-items-center`}
              onClick={() => {
                // --- Check if have some Reward be Editin' --- //
                if (isEditing) {
                  return pushToastify({
                    message: "Salve as edições antes de mudar de prêmio!",
                    type: "warning",
                    theme: "dark",
                  });
                }

                setActiveTab(index);
              }}
              style={{ cursor: "pointer", minWidth: "120px" }}
            >
              <i
                className="fas fa-grip-vertical me-2 text-muted"
                style={{ cursor: "grab" }}
              ></i>
              <span className="badge bg-primary me-2">{reward.order}º</span>
              <span className="text-truncate" style={{ maxWidth: "100px" }}>
                {reward.name || `Prêmio ${reward.order}`}
              </span>
            </button>
          </div>
        ))}
      </div>

      {/* Current Reward Card */}
      {currentReward && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <span className="badge bg-primary me-2">
                {currentReward.order}º
              </span>
              {currentReward.name || `Prêmio ${currentReward.order}`}
            </h5>
            <div className="btn-group">
              {!isEditing ? (
                <>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-primary btn-sm rounded"
                      onClick={() => handleEditReward(currentReward)}
                      title="Editar prêmio"
                    >
                      <i className="fas fa-edit me-1"></i>
                      Editar
                    </button>

                    {rewards.length > 1 && (
                      <button
                        className="btn btn-danger btn-sm rounded"
                        onClick={() => void handleDeleteReward(currentReward.order)}
                        title="Excluir prêmio"
                      >
                        <i className="fas fa-trash me-1"></i>
                        Excluir
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-success btn-sm rounded"
                      onClick={handleSaveReward}
                    >
                      Salvar este Prêmio
                    </button>
                    <button
                      className="btn btn-secondary btn-sm rounded"
                      onClick={() => setIsModalOpen(true)}
                    >
                      Mídias
                    </button>
                    <button
                      className="btn btn-danger btn-sm rounded"
                      onClick={handleCancelEdit}
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card-body">
            <div className="row">
              {/* Thumb */}
              {isEditing && (
                <>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label
                        htmlFor="campaignThumb"
                        className="form-label fw-bold"
                      >
                        Capa da Campanha (Thumbnail)
                      </label>
                      <ImageThumbUploader
                        onImageChange={(base64) => {
                          isEditing && handleInputChange("thumb", base64 || "");
                        }}
                        initialImage={editingReward?.thumb || ""}
                        resizeWidth={800}
                        resizeHeight={800}
                        maxKbSize={2048}
                        convertToWebp={true}
                      />
                      <button
                        className="btn btn-secondary btn-sm rounded w-100 mt-2"
                        onClick={() => setIsModalOpen(true)}
                      >
                        Adicionar Mídias
                      </button>
                    </div>
                  </div>

                  <div className="col-md-6 my-auto">
                    {/* Reward Category */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">
                        Categoria do Prêmio *
                      </label>
                      <RichSelect
                        label=""
                        options={rewardCategories}
                        value={
                          editingReward?.category ?? currentReward.category
                        }
                        onChange={(value) => {
                          if (!Array.isArray(value) && isEditing) {
                            handleInputChange("category", value);
                          }
                        }}
                        placeholder={
                          isLoadingRewardCategories
                            ? "Carregando categorias..."
                            : "Selecione a categoria"
                        }
                        isDisabled={!isEditing || isLoadingRewardCategories}
                      />
                    </div>

                    {/* Reward Condition */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">
                        Estado do Produto *
                      </label>
                      <RichSelect
                        label=""
                        options={selectRewardConditions}
                        value={
                          editingReward?.condition ?? currentReward.condition
                        }
                        onChange={(value) => {
                          if (!Array.isArray(value) && isEditing) {
                            handleInputChange("condition", value);
                          }
                        }}
                        placeholder="Estado do Produto"
                        isDisabled={!isEditing}
                      />
                    </div>

                    {/* Reward Product Real Price */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">Preço Pago</label>
                      <InputCurrency
                        animated={false}
                        label=""
                        description="* Preço de aquisição do prêmio (apenas p/ cálculo de lucro líquido e não será exibido aos usuários)"
                        placeholder="0,00"
                        prefix="R$"
                        value={
                          editingReward?.productCost ??
                          currentReward.productCost
                        }
                        onChangeTextOut={(v) =>
                          isEditing && handleInputChange("productCost", v)
                        }
                        min={0}
                        readOnly={!isEditing}
                        errorMessage={RaffleCreateIsValidRewardPrice({
                          price:
                            editingReward?.productCost ??
                            currentReward.productCost,
                          min: 0.5,
                          max: 9999999,
                        })}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Reward Name */}
              <div className="col-md-8 mb-3">
                <label className="form-label fw-bold">Nome do Prêmio *</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingReward?.name ?? currentReward.name}
                  onChange={(e) =>
                    isEditing && handleInputChange("name", e.target.value)
                  }
                  placeholder="Digite o nome do prêmio"
                  disabled={!isEditing}
                />
              </div>

              {/* Reward Price */}
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Preço de Mercado *</label>
                <InputCurrency
                  animated={false}
                  label=""
                  description="* Preço atual do produto no mercado"
                  placeholder="0,00"
                  prefix="R$"
                  value={editingReward?.price ?? currentReward.price}
                  onChangeTextOut={(v) =>
                    isEditing && handleInputChange("price", v)
                  }
                  min={0}
                  disabled={!isEditing}
                  errorMessage={RaffleCreateIsValidRewardPrice({
                    price: editingReward?.price ?? currentReward.price,
                    min: 0.5,
                    max: 9999999,
                  })}
                />
              </div>

              {/* Description Short */}
              <div className="col-md-12 mb-3">
                <label className="form-label fw-bold">Descrição Curta</label>
                <input
                  type="text"
                  className="form-control"
                  value={
                    editingReward?.description_short ??
                    currentReward.description_short
                  }
                  onChange={(e) =>
                    isEditing &&
                    handleInputChange("description_short", e.target.value)
                  }
                  placeholder="Breve descrição do prêmio"
                  maxLength={100}
                  readOnly={!isEditing}
                />
                {isEditing && (
                  <div className="form-text">
                    {editingReward?.description_short?.length || 0}/100
                    caracteres
                  </div>
                )}
              </div>

              {/* Reward Description */}
              <div className="col-12 mb-3">
                <label className="form-label fw-bold">Descrição Completa</label>
                <TextAreaQuill
                  key={`quill-${currentReward.order}`}
                  text={editingReward?.description ?? currentReward.description}
                  onChange={(content) => {
                    isEditing && handleInputChange("description", content);
                  }}
                  readOnly={!isEditing}
                />

                {isEditing && (
                  <div className="form-text">
                    {editingReward?.description?.length || 0}/500 caracteres
                  </div>
                )}
              </div>

              {editingReward && (
                <div className="col-12 mb-3 d-flex justify-content-center gap-3">
                  <button
                    className="btn btn-success btn-sm rounded"
                    onClick={handleSaveReward}
                  >
                    Salvar este Prêmio
                  </button>
                  <button
                    className="btn btn-danger btn-sm rounded"
                    onClick={handleCancelEdit}
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Estilos inline para drag & drop */}
      <style>
        {`
          .draggable-rewards-container .nav-item:active {
            cursor: grabbing !important;
          }
          
          .draggable-rewards-container .nav-item:hover {
            transform: translateY(-1px);
            transition: transform 0.2s ease;
          }
          
          .draggable-rewards-container .nav-item.opacity-50 {
            opacity: 0.5 !important;
          }
          
          .draggable-rewards-container .border-primary.border-2 {
            border-color: #0d6efd !important;
            border-width: 2px !important;
            background-color: rgba(13, 110, 253, 0.1);
          }
        `}
      </style>

      <ModalRaffleChangeRewardMedias
        medias={currentReward.medias}
        isOpen={isModalOpen}
        onClose={(updatedMedias) => {
          console.log({ updatedMedias });
          if (updatedMedias && editingReward) {
            setEditingReward({
              ...editingReward,
              medias: updatedMedias,
            });
          }
          setIsModalOpen(false);
        }}
      />
    </div>
  );
};
